/**
 * Deterministic per-tenant colour mapping.
 *
 * Our data model carries no brand colour for a tenant/organization, but
 * several screens (tenant switcher, cross-tenant device tables, the
 * software-update assignment/rollout screens) need a colour accent that is
 * stable for a given tenant everywhere it appears. We derive it from the
 * tenant id via a small hash into the design system's colourscale hues, so
 * the same id always maps to the same hue without any server-side state.
 *
 * Colour is always a secondary cue — every screen must also show the tenant
 * name as text.
 */

const HUES = [
  'blue',
  'cyan',
  'green',
  'magenta',
  'orange',
  'purple',
  'red',
  'yellow',
] as const;

export type TenantHue = (typeof HUES)[number];

export interface TenantAccent {
  /** colourscale-500 — dots, left-accent bars, progress-bar segments */
  dot: string;
  /** colourscale-100 — pill background fill */
  pillBg: string;
  /** colourscale-700 — pill text colour */
  pillFg: string;
}

// Stable index into HUES from a string (small FNV-ish hash). Keeps the same
// non-negative-index trick as the original tenant-switcher implementation:
// ((h % n) + n) % n is safe for all int32, whereas Math.abs(INT_MIN) is not.
function hashIndex(key: string, n: number): number {
  let h = 0;
  for (let i = 0; i < key.length; i++) {
    h = (h * 31 + key.charCodeAt(i)) | 0;
  }
  return ((h % n) + n) % n;
}

/** The hue name (e.g. for CSS class composition) for a given tenant id. */
export function tenantHue(tenantId: string): TenantHue {
  return HUES[hashIndex(tenantId, HUES.length)];
}

/** colourscale-500 CSS var for a tenant id — dots, accent bars, chart segments. */
export function tenantColor(tenantId: string): string {
  return `var(--colorscales-${tenantHue(tenantId)}-500)`;
}

/** Full {dot, pillBg, pillFg} token set for a tenant id, for pills/badges. */
export function tenantAccent(tenantId: string): TenantAccent {
  const hue = tenantHue(tenantId);
  return {
    dot: `var(--colorscales-${hue}-500)`,
    pillBg: `var(--colorscales-${hue}-100)`,
    pillFg: `var(--colorscales-${hue}-700)`,
  };
}
